// src/portal-app/more/DevicesScreen.js
//
// Plan 4 Task 11 — Devices screen, migrated to the P4 pattern (spec §5).
// See operations/TransactionsScreen.js:32-55 for the pattern this follows;
// this header only calls out decisions specific to Devices. At 296
// pre-migration lines this is the smallest P4 screen, with no FilterRow and
// no SearchInput to collapse — see "No control row" below.
//
// FIELD BINDING WAS WRONG — verified against the real backend before
// touching anything (the same class of defect Task 10 found: a field read
// that never existed on the real payload). GET /devices/ and GET /devices/me
// (backend/app/routers/devices.py:17-54, 83-119) serialise the SyncToken
// model (backend/app/models/models.py:59-69) as:
//   { id, device_name, created_at, last_used_at, expires_at, is_active, status }
// where `status` is backend-computed as 'active' | 'expired' | 'revoked'.
// There is no device_type, no station_name, no enrolled_at, no last_active,
// and the id field is `id`, not `device_id`. The pre-migration screen (and
// its test fixtures, which invented a matching-but-fictional shape) read
// device.device_id, device.device_type, device.station_name,
// device.last_active, device.enrolled_at and currentDevice?.device_id —
// EVERY one of these evaluated to undefined in production:
//   - the POS/Portal type badge and the station meta row never had real
//     data; both are dropped here, there is nothing to bind them to.
//   - isCurrent computed `device.device_id === currentDeviceId` where
//     `currentDeviceId = currentDevice?.device_id ?? null`. Since
//     currentDevice.device_id doesn't exist, `undefined ?? null` -> null,
//     so isCurrent was `undefined === null` -> ALWAYS false. The "This
//     device" badge never rendered and the self-protection guard never
//     applied to any device, including the one actually signed in.
//   - handleRevokeConfirm called `revokeDevice(revokeTarget.device_id)` —
//     always undefined — so every real revoke tap hit
//     DELETE /devices/undefined, which 422s against the route's
//     `device_id: int` path type. Revoke has never worked from this screen.
// This migration binds every field to the verified real shape (device.id,
// device.last_used_at, device.status) and, as a direct consequence, the
// self-protection guard and the revoke action both work correctly for the
// first time — not a behaviour change so much as the screen's already-
// declared intent finally being reachable.
//
// PortalDevicesPage.js (frontend/src/pages/portal/PortalDevicesPage.js) is
// the correctly-wired web equivalent of this exact endpoint and was used to
// cross-check the field names above; several t() keys are reused from it
// (see below) since they're already migrated for that page.
//
// Hero — "registered device count, with how many are currently online"
// (task brief). Value is the total row count (every status, since every
// status is visible in the list — same denominator as the rows below, in
// every SurfaceStates branch); caption states the online count in WORDS
// ("N online"), not just via the row pills' colour. Outside SurfaceStates
// so it survives empty/error (this screen's only "control" — see below —
// so it's also this screen's only mutation-tested survivor; see the test
// file and task-11-report.md for the move-inside/confirm-fail/revert proof).
//
// No control row — the brief explicitly frames the P4 control row as a
// maximum, not a quota. This screen has no categorical filter today (status
// is informational, not something a manager would filter a handful of
// devices by) and a device roster does not routinely exceed the ~50-item
// threshold the recipe sets for promoting search out of the nav-bar icon.
// No FilterButton/FilterSheet/SearchInput were added.
//
// Row — CondensedRow: name (device_name, falling back to t('unknown_device')
// since the column is nullable), last-seen as secondary (last_used_at,
// relative; 'Never' when null OR unparseable — see fmtLastSeen), status pill
// (online/expired/revoked, tone + WORD label — colour is never the sole
// carrier of meaning, spec §9). No `value` — there is no natural headline
// number for a device row (same call EmployeesScreen made for its rows).
//
// Status vocabulary — three states, not a strict binary, because the
// backend already computes three and collapsing 'expired' and 'revoked'
// into one generic "Offline" word would be an information loss the brief's
// own "online/offline must not be colour-only" instruction argues against:
// each state keeps its own word (Online / Expired / Revoked), which still
// satisfies "online" as the single positive state the hero counts.
//
// Self-protection, extended — revoke is hidden for the current device (as
// before, now actually working — see above) AND for any non-current device
// whose status isn't 'active'. This is a real UI consequence of exposing
// the real status for the first time: a "Revoke" button next to a row
// already labelled "Revoked"/"Expired" has nothing left to do (revoking an
// already-inactive token is a no-op) and reads as broken. Mirrors
// PortalDevicesPage's own `status === 'active'` gate on the same data.
//
// Enrolment date dropped from the row — the brief's row spec is exactly
// name / last-seen / status pill; CondensedRow has no natural home for a
// second date next to last-seen without composing it into `secondary` and
// pushing last-seen (the field the brief actually asks for) out. Same
// reasoning ProductsScreen/InventoryScreen used to drop cost from their own
// rows: back-office metadata, not a scan-time need, and not shown anywhere
// on this screen (disclosed here per lesson, not silently lost).
//
// PullToRefresh (Task 2) wraps the count line + list; this screen never had
// its own overflow-y:auto wrapper, so there is nothing to remove.
//
// Stale-response guard (seqRef): unchanged behaviour — incremented on every
// load(); load() is async and returns its promise so PullToRefresh can
// await it (previously a plain function; converting it to async doesn't
// change what fires or when — the effect that calls it on mount is
// untouched, and the two service calls and their Promise.all shape are
// identical to before).
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, {
    useState, useEffect, useCallback, useRef,
} from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { deviceService } from '../../services/api';
import { SurfaceStates } from '../insights/kit';
import {
    ConfirmSheet,
    CondensedRow,
    HeroStat,
    PullToRefresh,
    ensureKitCss,
    IconDevice,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                             */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.dev-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding:8px 0 80px;}

/* ---- hero ---- */
.dev-hero{margin:0 16px 16px;padding:18px 20px 16px;}

/* ---- count line ---- */
.dev-count-line{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin:2px 16px 10px;}

/* ---- device list ---- */
.dev-list{display:flex;flex-direction:column;gap:10px;padding:0 16px;}
.dev-row-wrap{display:flex;flex-direction:column;gap:6px;}

/* ---- current-device tag — replaces the revoke action row for the
   signed-in device; text-based, no reliance on colour alone. ---- */
.dev-current-tag{align-self:flex-start;margin-inline-start:44px;
  display:inline-flex;align-items:center;padding:4px 10px;border-radius:999px;
  font-size:11px;font-weight:700;letter-spacing:.3px;
  background:color-mix(in srgb,var(--c-green) 12%,transparent);
  color:var(--c-green);}

/* ---- revoke action ---- */
.dev-action-row{margin-inline-start:44px;}
.dev-revoke-btn{min-height:44px;padding:0 16px;border-radius:12px;
  border:1.5px solid color-mix(in srgb,var(--c-red) 40%,transparent);
  background:color-mix(in srgb,var(--c-red) 8%,transparent);
  font-family:inherit;font-size:13px;font-weight:700;color:var(--c-red);
  cursor:pointer;transition:transform .12s ease,background .12s ease;}
.dev-revoke-btn:active{transform:scale(.96);}
.dev-revoke-btn:hover{background:color-mix(in srgb,var(--c-red) 14%,transparent);}

/* ---- revoke error — rendered INSIDE ConfirmSheet as children (its real
   error slot; a sibling would sit behind the sheet's fixed backdrop). ---- */
.dev-revoke-error{font-size:12px;font-weight:600;color:var(--c-red);
  margin-top:4px;padding:0 4px;}

@media (prefers-reduced-motion:reduce){
  .dev-revoke-btn,.dev-revoke-btn:active{transition:none;transform:none;}
}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'dev-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Status vocabulary — see file header for why three states, not a     */
/* strict binary.                                                      */
/* ------------------------------------------------------------------ */
const STATUS_TONE = { active: 'green', expired: 'amber', revoked: 'red' };

function deviceStatus(device) {
    return device.status || (device.is_active ? 'active' : 'revoked');
}

function statusLabel(status, t) {
    if (status === 'active') return t('online', {}, 'Online');
    if (status === 'expired') return t('status_expired', {}, 'Expired');
    return t('status_revoked', {}, 'Revoked');
}

/* ------------------------------------------------------------------ */
/* Relative last-seen helper — binds to the real `last_used_at` field  */
/* (see file header). Returns 'Never' for both a missing timestamp     */
/* (device has never connected) and an unparseable one (bad data),     */
/* rather than a bare dash or an "Invalid Date" string.                */
/* ------------------------------------------------------------------ */
function fmtLastSeen(isoString, t) {
    if (!isoString) return t('never', {}, 'Never');
    const then = new Date(isoString).getTime();
    if (Number.isNaN(then)) return t('never', {}, 'Never');
    const diff = Date.now() - then;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    if (mins < 2) return t('just_now', {}, 'Just now');
    if (mins < 60) return t('n_minutes_ago', { n: mins }, `${mins} minutes ago`);
    if (hours < 24) return t('n_hours_ago', { n: hours }, `${hours === 1 ? '1 hour' : `${hours} hours`} ago`);
    if (days === 1) return t('yesterday', {}, 'Yesterday');
    return t('n_days_ago', { n: days }, `${days} days ago`);
}

/* ------------------------------------------------------------------ */
/* Device row                                                          */
/* ------------------------------------------------------------------ */
function DeviceRow({ device, isCurrent, onRevoke, t }) {
    const status = deviceStatus(device);
    const tone = STATUS_TONE[status] || 'muted';
    const name = device.device_name || t('unknown_device', {}, 'Unknown device');
    const secondary = fmtLastSeen(device.last_used_at, t);
    const canRevoke = !isCurrent && status === 'active';

    return (
        <div className="dev-row-wrap" data-testid={`device-card-${device.id}`}>
            <CondensedRow
                icon={<IconDevice />}
                tone={tone}
                name={name}
                secondary={secondary}
                pill={{ label: statusLabel(status, t), tone }}
                testId={device.id}
            />
            {isCurrent && (
                <div className="dev-current-tag" data-testid={`current-badge-${device.id}`}>
                    {t('this_device', {}, 'This device')}
                </div>
            )}
            {canRevoke && (
                <div className="dev-action-row">
                    <button
                        type="button"
                        className="dev-revoke-btn"
                        data-testid={`revoke-btn-${device.id}`}
                        onClick={() => onRevoke(device)}
                        aria-label={`${t('revoke_device', {}, 'Revoke')} ${name}`}
                    >
                        {t('revoke_device', {}, 'Revoke')}
                    </button>
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Main screen                                                          */
/* ------------------------------------------------------------------ */
export default function DevicesScreen() {
    ensureCss();
    ensureKitCss();
    const { t } = useLanguage();

    const [status, setStatus] = useState('loading');
    const [devices, setDevices] = useState([]);
    const [currentDeviceId, setCurrentDeviceId] = useState(null);

    // Revoke sheet state
    const [revokeTarget, setRevokeTarget] = useState(null); // Device object
    const [revokeLoading, setRevokeLoading] = useState(false);
    const [revokeError, setRevokeError] = useState(null);

    // Stale-response guard
    const seqRef = useRef(0);

    const load = useCallback(async () => {
        seqRef.current += 1;
        const seq = seqRef.current;
        setStatus('loading');
        try {
            const [deviceList, currentDevice] = await Promise.all([
                deviceService.listDevices(),
                deviceService.getCurrentDevice().catch(() => null),
            ]);
            if (seq !== seqRef.current) return; // stale
            const list = Array.isArray(deviceList) ? deviceList : [];
            setDevices(list);
            setCurrentDeviceId(currentDevice?.id ?? null);
            setStatus(list.length === 0 ? 'empty' : 'ready');
        } catch {
            if (seq !== seqRef.current) return;
            setStatus('error');
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleRevoke = (device) => {
        setRevokeTarget(device);
        setRevokeError(null);
    };

    const handleRevokeConfirm = async () => {
        if (!revokeTarget) return;
        setRevokeLoading(true);
        setRevokeError(null);
        try {
            await deviceService.revokeDevice(revokeTarget.id);
            setRevokeTarget(null);
            setRevokeLoading(false);
            load(); // reload list
        } catch {
            setRevokeLoading(false);
            setRevokeError(t('revoke_error', {}, 'Failed to revoke device. Please try again.'));
        }
    };

    const handleRevokeClose = () => {
        if (revokeLoading) return;
        setRevokeTarget(null);
        setRevokeError(null);
    };

    // ── Derived: hero + count line (same denominator — every device, every
    // status, matching every row rendered below in every SurfaceStates
    // branch) ──
    const total = devices.length;
    const onlineCount = devices.filter((d) => deviceStatus(d) === 'active').length;

    const ptrDisabled = !!revokeTarget;

    return (
        <div className="dev-wrap">
            {/* Hero card — registered count + online count, OUTSIDE
                SurfaceStates so it never vanishes (this screen's only
                control — see file header). */}
            <GlassCard veil className="dev-hero" data-testid="devices-hero">
                <HeroStat
                    label={t('devices', {}, 'Devices')}
                    value={total}
                    caption={t('n_devices_online', { count: onlineCount }, '{count} online')}
                />
            </GlassCard>

            <PullToRefresh onRefresh={() => load()} disabled={ptrDisabled}>
                <SurfaceStates
                    status={status}
                    onRetry={load}
                    emptyTitle={t('no_devices', {}, 'No devices enrolled')}
                    emptyMsg={t('no_devices_msg', {}, 'No devices have been enrolled for this account.')}
                >
                    {/* Count line */}
                    <div className="dev-count-line" data-testid="devices-count-line">
                        {t('n_devices', { count: total }, '{count} Devices')}
                    </div>

                    <div className="dev-list">
                        {devices.map((device) => (
                            <DeviceRow
                                key={device.id}
                                device={device}
                                isCurrent={device.id === currentDeviceId}
                                onRevoke={handleRevoke}
                                t={t}
                            />
                        ))}
                    </div>
                </SurfaceStates>
            </PullToRefresh>

            <ConfirmSheet
                open={!!revokeTarget}
                onClose={handleRevokeClose}
                onConfirm={handleRevokeConfirm}
                title={t('revoke_device', {}, 'Revoke Device')}
                message={t(
                    'revoke_device_confirm',
                    {},
                    'This device will be signed out and will need to re-enroll.',
                )}
                confirmLabel={t('revoke_device', {}, 'Revoke')}
                destructive
                loading={revokeLoading}
            >
                {revokeError && (
                    <div className="dev-revoke-error" data-testid="revoke-error">
                        {revokeError}
                    </div>
                )}
            </ConfirmSheet>
        </div>
    );
}
